import { Button, ResetIconButton } from '@library/components/buttons';
import {
  DEMONS_AND_GOBLINS_OPTIONS,
  DNP_OPTIONS,
  IN_GAME_OPTIONS,
  IS_SAME_TEAM_OPTIONS,
  MORE_OR_LESS_OPTIONS,
  REBOOT_OPTIONS
} from '@bo-constants/filters';
import { Filter, MultiSelectFilter } from '@library/components/filters';
import type { FilterKey, OptionalFilter } from '@library/types/filters';
import {
  NLS,
  convertLeagueProjectionAttributesToSelectOptions,
  gameStatusOptions,
  getGameOptions,
  getGamesTeamOptions,
  getPositionOptions
} from '@library/component-utils/filters';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { LeagueGames } from '@library/types/games';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import type { SelectOptions } from '@library/types/utils';
import { useGameModes } from '@bo-hooks/gameModes/useGameModes';
import { useLeagueDependentFiltersActive } from '@bo-hooks/filters/useLeagueDependentFiltersActive';

export interface LeagueDependentFiltersProps {
  className?: string;
  correlationsFilter?: boolean;
  disabled?: boolean;
  filters: OptionalFilter;
  games?: LeagueGames;
  handleCorrelationsChange?: (
    filterName: FilterKey,
    value: string | Array<string>
  ) => void;
  handleDNPChange?: (value: string) => void;
  handleGamesChange?: (value: Array<string>) => void;
  handleInGameChange?: (value: string) => void;
  handleDemonsAndGoblinsChange?: (value: string) => void;
  handlePositionsChange?: (value: Array<string>) => void;
  handleRebootChange?: (value: string) => void;
  handleStatTypeChange?: (value: Array<string>) => void;
  handleStatusChange?: (value: Array<string>) => void;
  handleTeamsChange?: (value: Array<string>) => void;
  handleGameModesChange?: (value: Array<string>) => void;
  nlsGameOption?: boolean;
  onReset?: () => void;
  positionValues?: Array<LeagueProjectionAttributes>;
  showFinished?: boolean;
  statTypeValues?: Array<LeagueProjectionAttributes>;
  showDrawer?: boolean;
  handleOpenDrawer?: () => void;
}

export const LeagueDependentFilters = ({
  className = '',
  correlationsFilter = false,
  disabled = false,
  filters,
  games = [],
  handleCorrelationsChange = () => {},
  handleDNPChange = () => {},
  handleDemonsAndGoblinsChange = () => {},
  handleGamesChange = () => {},
  handleInGameChange = () => {},
  handlePositionsChange = () => {},
  handleRebootChange = () => {},
  handleStatTypeChange = () => {},
  handleStatusChange = () => {},
  handleTeamsChange = () => {},
  handleGameModesChange = () => {},
  onReset = () => {},
  positionValues = [],
  showFinished = true,
  statTypeValues = [],
  nlsGameOption = false,
  showDrawer = false,
  handleOpenDrawer = () => {}
}: LeagueDependentFiltersProps) => {
  const {
    demonsAndGoblins: selectedDemonsAndGoblins,
    dnp: selectedDNP,
    firstChoice: selectedFirstChoice,
    gameModes: selectedGameModes,
    games: selectedGames,
    inGame: selectedInGame,
    isSameTeam: selectedIsSameTeam,
    positions: selectedPositions,
    position1: selectedPosition1,
    position2: selectedPosition2,
    reboot: selectedReboot,
    secondChoice: selectedSecondChoice,
    statType: selectedStat,
    statType1: selectedStat1,
    statType2: selectedStat2,
    status: selectedStatus,
    teams: selectedTeams
  } = filters;

  const { activeFilters } = useLeagueDependentFiltersActive({
    filters
  });

  const {
    demonsAndGoblins: demonsAndGoblinsActive,
    dnp: dnpActive,
    gameModes: gameModesActive,
    games: gamesActive,
    inGame: inGameActive,
    positions: positionsActive,
    reboot: rebootActive,
    statType: statsActive,
    status: statusesActive,
    teams: teamsActive
  } = activeFilters;

  // Correlation filters
  const {
    firstChoice: firstChoiceActive,
    isSameTeam: isSameTeamActive,
    position1: position1Active,
    position2: position2Active,
    secondChoice: secondChoiceActive,
    statType1: stat1Active,
    statType2: stat2Active
  } = activeFilters;

  const { gameOptions, gameValues } = getGameOptions({
    games,
    nlsGameOption
  });

  const gameTeamOptions = getGamesTeamOptions({
    games: selectedGames?.length ? gameValues : games,
    selectedGameIds: selectedGames
  });

  const positionOptions = getPositionOptions(positionValues);

  const statOptions: SelectOptions =
    convertLeagueProjectionAttributesToSelectOptions(
      statTypeValues,
      correlationsFilter
    );

  const isNLSGameFilter =
    gameOptions.length === 1 && gameOptions[0].value === NLS;

  const { gameModeSelectOptions: gameModeOptions } = useGameModes({});

  const handleCorrelationFilterChange = (
    filterName: FilterKey,
    value: string | Array<string>
  ) => {
    handleCorrelationsChange(filterName, value);
  };

  return (
    <div
      className={
        className || 'text-soClean-100 col-span-11 flex h-fit w-full gap-4'
      }
      data-testid="LeagueDependentFilters"
    >
      {gamesActive &&
        selectedGames &&
        (isNLSGameFilter ? (
          <Filter
            allOption={false}
            disabled
            onChange={(game) => handleGamesChange([game])}
            label="Games"
            options={gameOptions}
            selectedValue="NLS"
            testId="games-filter"
          />
        ) : (
          <MultiSelectFilter
            disabled={disabled}
            onChange={handleGamesChange}
            options={gameOptions}
            label="Games"
            selectedValues={selectedGames}
            testId="games-filter"
          />
        ))}
      {teamsActive && selectedTeams && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={handleTeamsChange}
          options={gameTeamOptions}
          label="Teams"
          selectedValues={selectedTeams}
          testId="teams-filter"
        />
      )}
      {gameModesActive && selectedGameModes && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={handleGameModesChange}
          options={gameModeOptions}
          label="Game Modes"
          returnAll
          selectedValues={selectedGameModes}
          testId="game-modes-filter"
        />
      )}
      {statsActive && selectedStat && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={handleStatTypeChange}
          label="Stat Types"
          options={statOptions}
          returnAll
          selectedValues={selectedStat}
          testId="stat-filter"
        />
      )}
      {statusesActive && selectedStatus && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={handleStatusChange}
          label="Statuses"
          returnAll
          options={
            showFinished
              ? gameStatusOptions
              : [
                  ...gameStatusOptions.slice(0, 2),
                  ...gameStatusOptions.slice(3, 5)
                ]
          }
          selectedValues={selectedStatus}
          testId="status-filter"
        />
      )}
      {positionsActive && selectedPositions && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={handlePositionsChange}
          options={positionOptions}
          label="Position"
          selectedValues={selectedPositions}
          testId="positions-filter"
        />
      )}
      {dnpActive && typeof selectedDNP === 'string' && (
        <Filter
          allOption
          disabled={disabled}
          onChange={handleDNPChange}
          options={DNP_OPTIONS}
          label="DNP Statuses"
          selectedValue={selectedDNP}
          testId="dnp-filter"
        />
      )}
      {rebootActive && typeof selectedReboot === 'string' && (
        <Filter
          allOption
          disabled={disabled}
          onChange={handleRebootChange}
          options={REBOOT_OPTIONS}
          label="Reboot Statuses"
          selectedValue={selectedReboot}
          testId="reboot-filter"
        />
      )}
      {inGameActive && typeof selectedInGame === 'string' && (
        <Filter
          allOption
          disabled={disabled}
          onChange={handleInGameChange}
          options={IN_GAME_OPTIONS}
          label="Projections"
          selectedValue={selectedInGame}
          testId="in-game-filter"
        />
      )}
      {demonsAndGoblinsActive &&
        typeof selectedDemonsAndGoblins === 'string' && (
          <Filter
            allOption
            disabled={disabled}
            onChange={handleDemonsAndGoblinsChange}
            options={DEMONS_AND_GOBLINS_OPTIONS}
            label="Odds Types"
            selectedValue={selectedDemonsAndGoblins}
            testId="odds-type-filter"
          />
        )}
      {/* Correlation Filters */}
      {stat1Active && correlationsFilter && selectedStat1 && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('statType1', value)
          }
          label="Stat Type 1"
          options={statOptions}
          selectedValues={selectedStat1}
          testId="stat-filter"
        />
      )}
      {stat2Active && correlationsFilter && selectedStat2 && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('statType2', value)
          }
          label="Stat Type 2"
          options={statOptions}
          selectedValues={selectedStat2}
          testId="stat-filter"
        />
      )}
      {position1Active && correlationsFilter && selectedPosition1 && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('position1', value)
          }
          options={positionOptions}
          label="Position 1"
          selectedValues={selectedPosition1}
        />
      )}
      {position2Active && correlationsFilter && selectedPosition2 && (
        <MultiSelectFilter
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('position2', value)
          }
          options={positionOptions}
          label="Position 2"
          selectedValues={selectedPosition2}
        />
      )}
      {isSameTeamActive && typeof selectedIsSameTeam === 'string' && (
        <Filter
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('isSameTeam', value)
          }
          options={IS_SAME_TEAM_OPTIONS}
          label="Is same team"
          selectedValue={selectedIsSameTeam}
        />
      )}
      {firstChoiceActive && typeof selectedFirstChoice === 'string' && (
        <Filter
          allOption
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('firstChoice', value)
          }
          options={MORE_OR_LESS_OPTIONS}
          label="First Choice 1"
          selectedValue={selectedFirstChoice}
        />
      )}
      {secondChoiceActive && typeof selectedSecondChoice === 'string' && (
        <Filter
          allOption
          disabled={disabled}
          onChange={(value) =>
            handleCorrelationFilterChange('secondChoice', value)
          }
          options={MORE_OR_LESS_OPTIONS}
          label="First Choice 2"
          selectedValue={selectedSecondChoice}
        />
      )}
      {showDrawer && (
        <Button
          className="bg-soFresh-110 hover:bg-soFresh-120 border-soFresh-140 text-soClean-140 flex size-10 min-w-3.5 items-center justify-center rounded-md border text-xs font-medium"
          data-testid="AllFiltersButton"
          onClick={handleOpenDrawer}
          type="button"
        >
          <AdjustmentsHorizontalIcon className="size-4 min-w-4" />
        </Button>
      )}
      <ResetIconButton
        disabled={disabled}
        label="Reset Filters"
        onClick={onReset}
      />
    </div>
  );
};
